import { Center, Loader, useMantineColorScheme } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  getColorSchemeOverride,
  getCurrencyOverride,
  getHeightOverride,
  getLocaleOverride,
  getWidthOverride,
} from "../../../shared/utils/embed-detection";
import type {
  NormalizedWidgetFeatures,
  WidgetAppearance,
  WidgetConfig,
  WidgetFeatures,
} from "../lib/config-schema";
import { normalizeFeatures } from "../lib/config-schema";
import { useConfig } from "../utils/config-loader";

interface WidgetConfigContextType {
  config: WidgetConfig;
  updateConfig: (updates: Partial<WidgetConfig>) => void;
  updateFeatures: (updates: Partial<WidgetFeatures>) => void;
  updateAppearance: (updates: Partial<WidgetAppearance>) => void;
  toggleFeature: (feature: "portfolio" | "staking" | "swap" | "defi") => void;
  resetConfig: () => void;
  features: NormalizedWidgetFeatures;
  appearance: WidgetAppearance;
}

const WidgetConfigContext = createContext<WidgetConfigContextType | undefined>(undefined);

export const WidgetConfigProvider = ({ children }: { children: ReactNode }) => {
  const { data: serverConfig, isLoading, error } = useConfig();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const { setColorScheme } = useMantineColorScheme();
  const hasInitializedColorScheme = useRef(false);

  // Get overrides from URL parameters (for customer embedding)
  // Use non-hook version since this provider is outside router context
  const urlColorScheme = getColorSchemeOverride();
  const urlWidth = getWidthOverride();
  const urlHeight = getHeightOverride();
  const urlCurrency = getCurrencyOverride();
  const urlLocale = getLocaleOverride();

  // Use server config directly or local config if it has been modified
  const currentConfig = config || serverConfig;

  // Create final config with URL parameter overrides applied
  const finalConfig = currentConfig
    ? {
        ...currentConfig,
        appearance: {
          ...currentConfig.appearance,
          // Override theme with URL parameter if provided
          theme: urlColorScheme || currentConfig.appearance.theme,
          // Override width with URL parameter if provided
          width: urlWidth !== null ? urlWidth : currentConfig.appearance.width,
          // Override height with URL parameter if provided
          height: urlHeight !== null ? urlHeight : currentConfig.appearance.height,
          // Override currency with URL parameter if provided
          currency: urlCurrency || currentConfig.appearance.currency,
          // Override locale with URL parameter if provided
          locale: urlLocale || currentConfig.appearance.locale,
        },
      }
    : null;

  // Initialize Mantine color scheme when config loads
  // Priority: URL override > Config theme > localStorage (user preference)
  useEffect(() => {
    if (hasInitializedColorScheme.current || !finalConfig?.appearance?.theme) {
      return;
    }

    // URL override takes highest priority
    if (urlColorScheme) {
      setColorScheme(urlColorScheme);
      localStorage.setItem("mantine-color-scheme", urlColorScheme);
      hasInitializedColorScheme.current = true;
      return;
    }

    const configTheme = finalConfig.appearance.theme;
    const storedColorScheme = localStorage.getItem("mantine-color-scheme");

    // Use config theme as default, but respect user preference if it differs
    if (storedColorScheme === "light" || storedColorScheme === "dark") {
      if (storedColorScheme !== configTheme) {
        // User has set a different preference, use it
        setColorScheme(storedColorScheme);
      } else {
        // Matches config, use config theme (ensures config changes are respected)
        setColorScheme(configTheme);
        localStorage.setItem("mantine-color-scheme", configTheme);
      }
    } else {
      // No user preference, use config theme
      setColorScheme(configTheme);
      localStorage.setItem("mantine-color-scheme", configTheme);
    }

    hasInitializedColorScheme.current = true;
  }, [finalConfig?.appearance?.theme, urlColorScheme, setColorScheme]);

  const updateConfig = (updates: Partial<WidgetConfig>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      ...updates,
    });
  };

  const updateFeatures = (updates: Partial<WidgetFeatures>) => {
    if (!currentConfig) return;
    setConfig({
      ...currentConfig,
      features: { ...currentConfig.features, ...updates },
    });
  };

  const updateAppearance = (updates: Partial<WidgetAppearance>) => {
    if (!currentConfig) return;

    setConfig({
      ...currentConfig,
      appearance: { ...currentConfig.appearance, ...updates },
    });
  };

  const toggleFeature = (feature: "portfolio" | "staking" | "swap" | "defi") => {
    if (!currentConfig) return;

    const currentValue = currentConfig.features[feature];

    // Compute proposed value preserving object form when present
    const proposedValue =
      typeof currentValue === "boolean"
        ? !currentValue
        : { ...currentValue, enabled: !currentValue.enabled };

    // Prevent disabling last enabled core feature
    const proposedFeatures = {
      ...currentConfig.features,
      [feature]: proposedValue,
    } as WidgetFeatures;
    const normalized = normalizeFeatures(proposedFeatures);
    const enabledCount = [
      normalized.portfolio,
      normalized.staking,
      normalized.swap,
      normalized.defi,
    ].filter(Boolean).length;
    if (enabledCount === 0) return;

    updateFeatures({ [feature]: proposedValue } as Partial<WidgetFeatures>);
  };

  const resetConfig = () => {
    // Reset to null and refetch from server
    setConfig(null);

    // Invalidate and refetch the query
    queryClient.invalidateQueries({ queryKey: ["config"] });
  };

  // Show loading state until config is loaded
  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // // If there's an error, throw it
  if (error) {
    throw new Error("Failed to load widget config");
  }

  if (!finalConfig) {
    throw new Error("Widget config not found");
  }

  return (
    <WidgetConfigContext.Provider
      value={{
        config: finalConfig,
        updateConfig,
        updateFeatures,
        updateAppearance,
        toggleFeature,
        resetConfig,
        features: normalizeFeatures(finalConfig.features),
        appearance: finalConfig.appearance,
      }}
    >
      {children}
    </WidgetConfigContext.Provider>
  );
};

export const useWidgetConfig = () => {
  const context = useContext(WidgetConfigContext);
  if (context === undefined) {
    throw new Error("useWidgetConfig must be used within a WidgetConfigProvider");
  }
  return context;
};

export const useFeatures = () => {
  const { features, toggleFeature, updateFeatures } = useWidgetConfig();
  return { features, toggleFeature, updateFeatures };
};

export const useAppearance = () => {
  const { appearance, updateAppearance } = useWidgetConfig();
  const { setColorScheme } = useMantineColorScheme();

  // Enhanced updateAppearance to also sync with Mantine colorScheme
  const enhancedUpdateAppearance = (updates: Partial<WidgetAppearance>) => {
    updateAppearance(updates);

    // If theme is being updated, sync with Mantine and localStorage
    if (updates.theme) {
      setColorScheme(updates.theme);
      localStorage.setItem("mantine-color-scheme", updates.theme);
    }
  };

  return {
    appearance,
    updateAppearance: enhancedUpdateAppearance,
  };
};
